import { Box, Paper, Stack } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { GlobalDataContext } from "../../../../contexts/GlobalData";
import { TEMPLATES_LIST } from "../../../../enums/QueryKeys";
import { fetchTemplates } from "../../../../enums/queryFunction/Fetch";
import { EDIT_TEMPLATES, DELETE_TEMPLATES } from "../../../../enums/PermissionTitles";
import useUser from "../../../../hooks/useUser";
import { useSearchParams } from "react-router-dom";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import TemplateCategoryFilter from "./TemplateCategoryFilter";
import TemplatesSearchFilter from "./TemplatesSearchFilter";
import TemplatesTable from "./TemplatesTable";
import TemplateDetailsDialog from "./TemplateDetailsDialog";
import TemplateFormDialog from "./TemplateFormDialog";
import DeleteTemplateDialog from "./DeleteTemplateDialog";
import { stripHtml } from "./TemplatesTable";

const STRING_SORT_FIELDS = new Set(["title", "description"]);

// Client-side sort, same reasoning as WarehouseServicesTab.jsx - Templates'
// server-side search has no accompanying sort param.
function compareTemplates(a, b, orderBy, order) {
  const direction = order === "desc" ? -1 : 1;
  const aValue = orderBy === "description" ? stripHtml(a?.[orderBy]) : a?.[orderBy];
  const bValue = orderBy === "description" ? stripHtml(b?.[orderBy]) : b?.[orderBy];
  if (!aValue && !bValue) return 0;
  if (!aValue) return 1;
  if (!bValue) return -1;
  if (STRING_SORT_FIELDS.has(orderBy)) {
    return direction * String(aValue).localeCompare(String(bValue));
  }
  return direction * (Number(aValue) - Number(bValue));
}

// The "Predlošci" tab's content, rebuilt to match Artikli/Usluge's table +
// details-dialog + form-dialog pattern - replaces the old
// SingleTemplateLink.jsx card list. Server-side title/category search
// (fetchTemplates) is kept as-is, just feeding a table instead of a list
// of cards.
const TemplatesTab = React.forwardRef(function TemplatesTab(props, ref) {
  const { currentClinic } = useUser();
  const clinicId = currentClinic?.id;
  const [searchQuery, setSearchQuery] = useSearchParams("");
  const titleParam = searchQuery.get("q") ?? "";
  const categoryParam = searchQuery.get("category") ?? "ALL";
  const {
    data: templates,
    isLoading,
    error,
  } = useQuery({
    queryKey: [TEMPLATES_LIST + clinicId, titleParam, categoryParam],
    queryFn: () =>
      fetchTemplates(
        clinicId,
        titleParam,
        categoryParam !== "ALL" ? categoryParam : null,
      ),
    staleTime: Infinity,
  });
  const queryClient = useQueryClient();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const isEditingAllowed = isPermissionAllowed(EDIT_TEMPLATES);
  const isDeletingAllowed = isPermissionAllowed(DELETE_TEMPLATES);

  const items = React.useMemo(() => (Array.isArray(templates) ? templates : []), [templates]);
  const isSearchActive = titleParam !== "" || categoryParam !== "ALL";

  // Persisted to the URL (not local state), same reasoning as
  // WarehouseServicesTab.jsx - survives refresh/back-navigation and makes
  // the sorted view shareable.
  const templateSortParam = searchQuery.get("templateSort") || "";
  const [orderBy, order] = templateSortParam
    ? templateSortParam.split("=")
    : ["title", "asc"];
  const sortedItems = React.useMemo(
    () => [...items].sort((a, b) => compareTemplates(a, b, orderBy, order)),
    [items, orderBy, order],
  );

  function handleSort(field, direction) {
    updateQueryParam("templateSort", `${field}=${direction}`);
  }

  const [detailsTemplateId, setDetailsTemplateId] = React.useState(null);
  const [formState, setFormState] = React.useState(null); // { template: null | template }
  const [templatePendingDelete, setTemplatePendingDelete] = React.useState(null);

  const detailsTemplate = items.find((item) => item.id === detailsTemplateId) ?? null;

  React.useImperativeHandle(ref, () => ({
    openAddDialog: () => setFormState({ template: null }),
  }));

  const updateQueryParam = React.useCallback(
    (key, value) => {
      setSearchQuery((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      });
    },
    [setSearchQuery],
  );

  function closeTemplateForm() {
    setFormState(null);
    queryClient.invalidateQueries({ queryKey: [TEMPLATES_LIST + clinicId] });
  }

  if (error) {
    return <SimpleAlert message="ErrorFetchingWarehouseItemsData" severity="error" />;
  }

  return (
    <>
      <Stack spacing={2} mb={3}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: "stretch",
            width: "100%",
          }}
        >
          <Paper
            variant="outlined"
            sx={{ p: 2, flex: 1, borderRadius: 3, borderColor: "divider" }}
          >
            <TemplatesSearchFilter
              initialValue={titleParam}
              onSearchChange={(val) => updateQueryParam("q", val)}
            />
          </Paper>
          <Box sx={{ minWidth: { xs: "100%", sm: 200 }, display: "flex" }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                width: "100%",
                display: "flex",
                alignItems: "center",
                borderRadius: 3,
                borderColor: "divider",
              }}
            >
              <TemplateCategoryFilter
                value={categoryParam}
                onChange={(e) =>
                  updateQueryParam(
                    "category",
                    e.target.value !== "ALL" ? e.target.value : "",
                  )
                }
              />
            </Paper>
          </Box>
        </Box>
      </Stack>

      <TemplatesTable
        items={sortedItems}
        isLoading={isLoading}
        emptyMessage={isSearchActive ? "NoTemplatesFoundForGivenQuery" : "NoTemplates"}
        onRowClick={(template) => setDetailsTemplateId(template.id)}
        order={order}
        orderBy={orderBy}
        onSort={handleSort}
      />

      <TemplateDetailsDialog
        template={detailsTemplate}
        canEdit={isEditingAllowed}
        canDelete={isDeletingAllowed}
        onClose={() => setDetailsTemplateId(null)}
        onEdit={(template) => {
          setDetailsTemplateId(null);
          setFormState({ template });
        }}
        onDelete={(template) => setTemplatePendingDelete(template)}
      />

      {formState && (
        <TemplateFormDialog
          open={Boolean(formState)}
          onClose={closeTemplateForm}
          clinicId={clinicId}
          initialValues={formState.template}
        />
      )}

      <DeleteTemplateDialog
        template={templatePendingDelete}
        clinicId={clinicId}
        onCancel={() => setTemplatePendingDelete(null)}
        onDeleted={() => setDetailsTemplateId(null)}
      />
    </>
  );
});

export default TemplatesTab;
